import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity, DocumentType } from "@erp/shared-types";

/**
 * Extract Document Content Step
 *
 * Processes document attachments associated with an inbound email
 * and extracts their content using OCR or document parsing.
 *
 * Filtering behavior (controlled by config):
 * - skipClassificationCheck: true → Process ALL documents regardless of classification
 * - skipClassificationCheck: false (default) → Skip documents classified as OTHER or unclassified
 * - filterForDocumentTypes: When specified, only process documents matching these types
 *
 * This ensures we only run expensive OCR operations on relevant documents.
 *
 * Supports multiple file types:
 * - PDF: ExtendAI or OpenAI Vision OCR
 * - Excel: ExcelJS parsing
 * - DOCX: Office parser
 * - Plaintext: Direct text extraction
 */
/**
 * Source-of-truth enum of Extend AI extraction schema names supported by this
 * step's config. Exported so frontend config renderers can derive their
 * dropdown options from the same list — preventing UI/backend drift.
 */
export const extendSchemaEnum = z.enum([
  "purchase_order",
  "ap_invoice",
  "remittance_advice",
  "lead_time_sheet",
  "carrier_confirmation",
  "bill_of_lading",
  "proof_of_delivery",
  "receiving_stamp",
]);

/**
 * Document type filter enum - matches DocumentType from shared-types
 */
const documentTypeEnum = z.enum(DocumentType);

export const extractDocumentContentStep = defineStepConfig({
  type: "extract_document_content",
  name: "Extract Document Content",
  description: "Run OCR and content extraction on document attachments",
  category: "Transformation",
  deprecated: {
    reason:
      "Use Extract Document Content V2 which supports admin-configurable extraction schemas",
    replacedBy: "extract_document_content_v2",
  },

  // TODO
  // Angel: this should probably take:
  // - inboundEmail | document
  // - extraction schema (the caller should fetch/augment this as needed)
  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email whose attachments should be processed",
    ),
    extractionConfig: z
      .object({
        type: z.literal("EXTRACT"),
        schema: z.record(z.string(), z.unknown()),
        advancedOptions: z.record(z.string(), z.unknown()).optional(),
      })
      .describe(
        "Optional extraction schema; defaults to Extend AI schema for the configured type",
      )
      .optional(),
    // Email origin metadata — passed through for downstream reply detection
    originType: z
      .string()
      .optional()
      .describe(
        'Email origin classification: "external", "self_sent", or "reply_to_outbound"',
      ),
    conversationId: z
      .string()
      .optional()
      .describe(
        "Microsoft Graph conversation/thread ID for tracing replies to orders",
      ),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email for downstream steps",
    ),
    originType: z
      .string()
      .optional()
      .describe("Pass-through of email origin classification"),
    conversationId: z
      .string()
      .optional()
      .describe("Pass-through of conversation/thread ID"),
    documents: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .describe(
        "Array of document entity references (for fanout to per-document processing)",
      ),
    documentOcrResults: z.array(
      z.object({
        document: entityOfType(SystemEntity.CORE_DOCUMENT),
        ocrResult: entityOfType(
          SystemEntity.CORE_DOCUMENT_OCR_RESULT,
        ).optional(),
        status: z.enum(["completed", "failed", "skipped"]),
        extractedTextLength: z.number().optional(),
        error: z.string().optional(),
      }),
    ),
    processedCount: z.number(),
    failedCount: z.number(),
    skippedCount: z.number(),
  }),

  configSchema: z.object({
    extendSchemaType: extendSchemaEnum.optional(),
    skipClassificationCheck: z
      .boolean()
      .optional()
      .describe(
        "Skip document classification checks entirely (processes all documents regardless of classification)",
      ),
    filterForDocumentTypes: z
      .array(documentTypeEnum)
      .optional()
      .describe(
        "Only process documents with these classification types. If specified, documents not matching these types are skipped. Overrides the default OTHER-exclusion behavior.",
      ),
    useClassificationBasedSchema: z
      .boolean()
      .optional()
      .describe(
        "Dynamically select extraction schema based on each document's classification type. When enabled, ignores the extendSchemaType config and determines the appropriate schema per document.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "15 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
    nonRetryableErrorTypes: [
      "InvalidFileFormatError",
      "UnsupportedMimeTypeError",
    ],
  },
});
